import { Box, Stack, Typography, useTheme } from '@mui/material'
import { alpha } from '@mui/material/styles'
import { TriangleAlertIcon } from 'lucide-react'
import { WON_WITHOUT_PAYING_TEXT, type PayingNowStatus } from '@repo/types'

import { StatusChip } from 'components/display/StatusChip/StatusChip'
import { InfoTooltip } from 'components/feedback/InfoTooltip'
import { statusFilterOptions } from 'components/input/filters/StatusChipFilter'
import { PAYING_NOW_CHIP_STATUSES } from 'constants/statuses'

import { shortDate } from '../InternalAnalyticsPage/components/shared'

/**
 * VRG-700 — the two marks a deal carries wherever it is listed (the board card, the Leads and
 * Companies rows, the company drawer), so the three surfaces cannot word them differently.
 * Both explain themselves through the shared InfoTooltip, with the glossary entry as the
 * footer (§44): `sales-auto-won` and `sales-won-without-paying`.
 */

/** "auto": Vera moved this deal to Won itself, at a subscription's first paid invoice (VRG-727). */
export const AutoWonBadge = ({ autoWon }: { autoWon: { subscription_id: string; at: string } }) => {
  const theme = useTheme()
  return (
    <InfoTooltip
      term="sales-auto-won"
      text={`Moved to Won automatically on ${shortDate(autoWon.at)}: subscription ${autoWon.subscription_id} paid its first invoice. Move it back by hand and Vera will not move it again for that subscription.`}
    >
      <Box
        component="span"
        data-testid="auto-won-badge"
        aria-label="Moved to Won automatically"
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          px: 1.5,
          height: 20,
          borderRadius: 1,
          border: `1px solid ${alpha(theme.palette.success.main, 0.4)}`,
          backgroundColor: alpha(theme.palette.success.main, 0.1),
          color: theme.palette.success.main,
          cursor: 'help',
          flexShrink: 0,
        }}
      >
        <Typography variant="body4" sx={{ fontWeight: 500, lineHeight: 1 }}>
          auto
        </Typography>
      </Box>
    </InfoTooltip>
  )
}

/** The warning on a Won deal whose company has nothing paying. */
export const WonWithoutPayingIcon = () => {
  const theme = useTheme()
  return (
    <InfoTooltip term="sales-won-without-paying" text={WON_WITHOUT_PAYING_TEXT}>
      <Box
        component="span"
        data-testid="won-without-paying"
        aria-label={WON_WITHOUT_PAYING_TEXT}
        sx={{ display: 'inline-flex', color: theme.palette.warning.main, cursor: 'help', flexShrink: 0 }}
      >
        <TriangleAlertIcon size={14} strokeWidth={1.5} />
      </Box>
    </InfoTooltip>
  )
}

/** Both, from a deal's marks; renders nothing for a deal that carries neither. */
export const DealMarks = ({
  wonWithoutPaying,
  autoWon,
}: {
  wonWithoutPaying: boolean
  autoWon: { subscription_id: string; at: string } | null | undefined
}) => (
  <>
    {autoWon && <AutoWonBadge autoWon={autoWon} />}
    {wonWithoutPaying && <WonWithoutPayingIcon />}
  </>
)

/**
 * VRG-727 — the company's Paying now status as a chip: Paying now (green) or Not paying (it paid
 * once and lapsed, orange). Never paid shows nothing on a row (most of the pipeline has never
 * paid, and a chip on every row would bury the two that matter); the filter still offers it.
 * One component for Sales → Companies, Deals and the drawer; Accounts → Companies uses the same
 * status map (`PAYING_NOW_CHIP_STATUSES`).
 */
export const PayingNowChip = ({ status }: { status?: PayingNowStatus | null }) =>
  status === 'paying_now' || status === 'not_paying' ? (
    <StatusChip size="small" statuses={PAYING_NOW_CHIP_STATUSES} status={status} />
  ) : null

/**
 * VRG-727 — the Paying now column on Sales → Companies and Deals and Accounts → Companies: the company's status as a chip
 * (Paying now / Not paying; Never paid is blank), filterable by all three as the same pills.
 */
export const payingNowColumn = <R,>(get: (row: R) => PayingNowStatus | null | undefined) => ({
  field: 'paying_now',
  headerName: 'Paying now',
  type: 'statusChip',
  options: statusFilterOptions(PAYING_NOW_CHIP_STATUSES),
  width: 150,
  valueGetter: (_v: unknown, row: R) => get(row) ?? 'never_paid',
  renderCell: ({ row }: { row: R }) => (
    <Stack direction="row" alignItems="center" height="100%">
      <PayingNowChip status={get(row)} />
    </Stack>
  ),
})
